// ./src/components/features/CreateEventWizard/steps/BudgetStep.tsx
//
// "What will it cost?" — shown when the Budget feature is on (ROADMAP A2b #62 P6(e)). Lines are made in the Budget page's
// own `BudgetItemEditDrawer` (a fixed amount, price × quantity, per head of the guest list…) under a provider scoped by
// the event; a one-tap starter only opens it with a name filled in (`wizardStarters.ts`). Payments are recorded later,
// on the Budget page.
//
// ⚠ A new line is stamped with the EVENT's currency, so the step reads the event first and Add waits for that read —
//   otherwise the drawer's fallback currency would be written onto the line of an event priced in another one.
// ⚠ An app-kept line (`isSystem` — the event's Staff pay) cannot be removed; its row offers Edit only, as the page does.

import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { BudgetItemsProvider, useBudgetItemsContext } from '@/contexts';
import { useCreateGate, useEvent, useToast } from '@/hooks';
import { BUDGET_CALC_TYPE, BudgetItem, WHOLE_SET } from '@/utils/api';
import { formatCurrency, refusalReason } from '@/utils';
import { ConfirmModal } from '@/components/generic';
import { BudgetItemEditDrawer, getCalcTypeLabel } from '@/components/features/Budget';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { BUDGET_STARTERS, startersFor, unusedStarters } from '../wizardStarters';
import { useTypeIdeas } from '../useTypeIdeas';

const BudgetStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { budgetItems, list, isLoaded, remove } = useBudgetItemsContext();
  const { event, error: eventError, reload: reloadEvent } = useEvent(eventId);
  // The budget is the `production` bundle — BudgetItemCore refuses a create the plan does not include.
  const gate = useCreateGate(FEATURE_CODES.PRODUCTION, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null; name?: string } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<BudgetItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The same fallback as the Budget page — reached only when the event has no currency at all.
  const eventCurrency = event?.currencyCode || 'USD';
  // The label is both the button and the saved name, so it is translated before anything compares it.
  // ⓘ The TYPE's own ideas when it has any (#62 P6(h)), else the generic ones.
  const typeIdeas = useTypeIdeas('budget');
  const starters = unusedStarters(
    startersFor(typeIdeas, BUDGET_STARTERS.map((s) => ({ ...s, label: t(`events:wizard.budget.starter.${s.key}`, { defaultValue: s.label }) })), 'Wallet'),
    budgetItems.map((item) => item.name),
  );

  const openNew = (name?: string) => {
    if (!gate.canCreate) {
      return;
    }

    setDrawer({ recordId: null, name });
  };

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  const subtitleOf = (item: BudgetItem): string => {
    const amount = formatCurrency(Number(item.expectedAmount) || 0, item.currencyCode || eventCurrency);

    return !item.calcType || item.calcType === BUDGET_CALC_TYPE.FIXED ? amount : `${amount} · ${getCalcTypeLabel(item.calcType)}`;
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.budget.intro', {
          defaultValue:
            'Note what you expect to spend — a fixed amount, a price times a quantity, or a cost per guest that follows your guest list. You record what is paid later, on the event’s Budget page.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.budget.title', { defaultValue: 'Budget' })}
        addLabel={t('events:wizard.budget.add', { defaultValue: 'Add a cost' })}
        onAdd={() => openNew()}
        gate={gate}
        gateWhat={t('events:wizard.budget.another', { defaultValue: 'another cost' })}
        disabled={saving || !event}
        starters={starters.map((s) => ({ key: s.key, label: s.label, icon: s.icon, onClick: () => openNew(s.label) }))}
        isLoaded={(isLoaded || !!readError) && (!!event || !!eventError)}
        readError={readError ?? eventError}
        onRetry={() => {
          void reloadEvent();
          void load();
        }}
        errorTitle={t('events:wizard.budget.loadFailed', { defaultValue: 'Could not load the budget' })}
        operation="budgetItemList"
        emptyText={t('events:wizard.budget.none', { defaultValue: 'No costs yet.' })}
        isEmpty={budgetItems.length === 0}
      >
        {budgetItems.map((item) => (
          <WizardItemRow
            key={item.id}
            icon="Wallet"
            title={item.name ?? ''}
            subtitle={subtitleOf(item)}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: item.id as string })}
            onRemove={item.isSystem ? undefined : () => setPendingRemove(item)}
          />
        ))}
      </WizardListSection>

      <BudgetItemEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        eventCurrency={eventCurrency}
        defaultName={drawer?.name ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.budget.removeTitle', { defaultValue: 'Remove this cost?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const BudgetStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <BudgetItemsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <BudgetStepBody eventId={createdEventId} />
    </BudgetItemsProvider>
  );
};
